import { useEffect, useState } from 'react';

/**
 * MSAL's event names (EventType in @azure/msal-browser), written out so this
 * package stays structural like startMsalApp and doesn't depend on MSAL.
 */
const LOGIN_SUCCESS = 'msal:loginSuccess';
const ACQUIRE_TOKEN_SUCCESS = 'msal:acquireTokenSuccess';
const LOGOUT_SUCCESS = 'msal:logoutSuccess';

/** The slice of MSAL's PublicClientApplication this needs. */
export interface MsalAccountSource<A> {
  getActiveAccount(): A | null;
  setActiveAccount(account: A | null): void;
  addEventCallback(callback: (event: { eventType: string; payload: unknown }) => void): string | null;
  removeEventCallback(callbackId: string): void;
}

/**
 * The signed-in account, for showing who is signed in (#291 — previously a
 * `useAccount` copied into every app's src/lib/msal.ts).
 *
 * Starts from the ACTIVE account only, the one startMsalApp chose before the
 * app rendered (the portal's login_hint, else the last-used account, else the
 * picker). Never the first cached account: on a shared device that is the
 * previous user (#242, #313).
 */
export function useMsalAccount<A>(msal: MsalAccountSource<A>): A | null {
  const [account, setAccount] = useState<A | null>(() => msal.getActiveAccount());

  useEffect(() => {
    const callbackId = msal.addEventCallback((event) => {
      if (event.eventType === LOGIN_SUCCESS || event.eventType === ACQUIRE_TOKEN_SUCCESS) {
        const payload = event.payload as { account?: A } | null;
        if (payload?.account) {
          msal.setActiveAccount(payload.account);
          setAccount(payload.account);
        }
      }
      if (event.eventType === LOGOUT_SUCCESS) setAccount(null);
    });
    return () => {
      if (callbackId) msal.removeEventCallback(callbackId);
    };
  }, [msal]);

  return account;
}
